import React, { useState, useMemo, useEffect } from 'react';
import {
  CheckCircle, Circle, AlertTriangle, ChevronDown, ChevronUp, Save,
  FileText, Anchor, Shield, Settings, Cloud, Users
} from 'lucide-react';
import { useVessel } from '../../../../contexts/VesselContext';
import { getRequiredChecks } from './PreDepartureRequirements';
import { parseCategory } from '../../../../utils/categoryHelpers';

/**
 * Regulation-based Pre-Departure Checklist
 * Implements actual UK maritime requirements from WBC3, MGN, MSN, etc.
 */
const RegulationPreDepartureChecklist = ({ voyageId, voyageDetails, onComplete }) => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const [isExpanded, setIsExpanded] = useState(true);
  const [checklistData, setChecklistData] = useState({});
  const [customChecks, setCustomChecks] = useState([]);
  
  // Get time of day for conditional checks
  const isNightTime = () => {
    const hour = new Date().getHours();
    return hour < 6 || hour > 18; // Simple night check
  };
  
  // Build vessel configuration for requirement checking
  const vesselConfig = useMemo(() => ({
    type: currentVessel?.type || 'workboat',
    grossTonnage: parseInt(currentVessel?.grossTonnage) || 0,
    lengthOverall: parseInt(currentVessel?.lengthOverall) || 0,
    personsCapacity: parseInt(currentVessel?.maxPersons || currentVessel?.personsCapacity) || 0,
    areaCategory: parseCategory(currentVessel?.category),
    maxSpeed: parseInt(currentVessel?.maxSpeed) || 0,
    operations: currentVessel?.operations || [],
    voyageType: voyageDetails?.international ? 'international' : 'domestic',
    isNight: isNightTime()
  }), [currentVessel, voyageDetails]);
  
  // Get required checks based on vessel configuration
  const requiredChecks = useMemo(() => {
    const checks = getRequiredChecks(vesselConfig);
    
    // Add night checks if applicable
    if (vesselConfig.isNight) {
      checks.push({
        id: 'night_nav_lights',
        label: 'Navigation lights - test all sectors',
        regulation: 'WBC3 Section 18.1',
        type: 'test',
        required: true,
        category: 'navigation'
      });
    }
    
    return checks;
  }, [vesselConfig]);
  
  // Initialize checklist data
  useEffect(() => {
    const saved = localStorage.getItem(`regulation_checklist_${voyageId}`);
    if (saved) {
      setChecklistData(JSON.parse(saved));
      return;
    }
    
    // Initialize with required checks
    const initialData = {};
    requiredChecks.forEach(check => {
      initialData[check.id] = {
        completed: false,
        value: '',
        timestamp: null
      };
    });
    setChecklistData(initialData);
  }, [voyageId, requiredChecks]);
  
  // Group checks by category
  const groupedChecks = useMemo(() => {
    const groups = {
      machinery: [],
      navigation: [],
      safety: [],
      weather: [],
      crew: [],
      documentation: [],
      operations: [],
      communications: [],
      security: [],
      other: []
    };
    
    requiredChecks.forEach(check => {
      const category = check.category || 'other';
      if (groups[category]) {
        groups[category].push(check);
      } else {
        groups.other.push(check);
      }
    });
    
    // Add custom checks
    customChecks.forEach(check => {
      groups.other.push(check);
    });
    
    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });
    
    return groups;
  }, [requiredChecks, customChecks]);
  
  // Update check status
  const updateCheck = (checkId, field, value) => {
    const newData = {
      ...checklistData,
      [checkId]: {
        ...checklistData[checkId],
        [field]: value,
        timestamp: field === 'completed' ? new Date().toISOString() : checklistData[checkId]?.timestamp
      }
    };
    setChecklistData(newData);
    localStorage.setItem(`regulation_checklist_${voyageId}`, JSON.stringify(newData));
  };
  
  // Calculate completion
  const calculateCompletion = () => {
    const required = requiredChecks.filter(c => c.required !== false);
    let completed = 0;
    
    required.forEach(check => {
      const data = checklistData[check.id];
      if (data?.completed) {
        // If it needs a value, check that too
        if (check.recordValue && !data.value) {
          return;
        }
        completed++;
      }
    });
    
    return {
      completed,
      total: required.length,
      percentage: required.length > 0 ? Math.round((completed / required.length) * 100) : 0
    };
  };
  
  const completion = calculateCompletion();
  const isComplete = completion.percentage === 100;
  
  // Add custom check
  const addCustomCheck = () => {
    const label = prompt('Enter custom check:');
    if (label) {
      const newCheck = {
        id: `custom_${Date.now()}`,
        label,
        category: 'other',
        custom: true,
        required: false
      };
      setCustomChecks([...customChecks, newCheck]);
      updateCheck(newCheck.id, 'completed', false);
    }
  };
  
  // Handle completion
  const handleComplete = () => {
    if (!isComplete) {
      alert('Please complete all required checks before departure');
      return;
    }
    
    // Create summary for log
    const summary = {
      checksCompleted: completion.completed,
      totalChecks: completion.total,
      weather: {},
      engine: {},
      timestamp: new Date().toISOString()
    };
    
    // Extract weather data
    ['wind_speed', 'wind_direction', 'sea_state', 'visibility'].forEach(id => {
      if (checklistData[id]?.value) {
        summary.weather[id] = checklistData[id].value;
      }
    });
    
    // Extract engine data
    ['oil_pressure', 'coolant_temp', 'fuel_level', 'engine_hours'].forEach(id => {
      if (checklistData[id]?.value) {
        summary.engine[id] = checklistData[id].value;
      }
    });
    
    onComplete(checklistData, summary);
  };
  
  // Get icon for category
  const getCategoryIcon = (category) => {
    switch(category) {
      case 'machinery': return <Settings className="h-4 w-4" />;
      case 'navigation': return <Anchor className="h-4 w-4" />;
      case 'safety': return <Shield className="h-4 w-4" />;
      case 'weather': return <Cloud className="h-4 w-4" />;
      case 'crew': return <Users className="h-4 w-4" />;
      case 'documentation': return <FileText className="h-4 w-4" />;
      default: return <CheckCircle className="h-4 w-4" />;
    }
  };
  
  // Format category name
  const formatCategory = (category) => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };
  
  return (
    <div className="bg-white rounded-lg shadow mb-4">
      <div className="px-3 py-2 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-0.5 hover:bg-gray-100 rounded"
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            <h3 className="text-sm font-medium text-gray-900">Pre-Departure Checklist</h3>
            <div className="flex items-center gap-2">
              {isComplete ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium">{completion.completed}/{completion.total}</span>
                  <span className="text-xs text-gray-500">({completion.percentage}%)</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span>{currentVessel?.type || 'Workboat'}</span>
            <span>{vesselConfig.grossTonnage} GT</span>
            <span>Cat {vesselConfig.areaCategory}</span>
            {vesselConfig.isNight && <span className="text-blue-600">Night</span>}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="p-3">
          {/* Grouped Checks */}
          <div className="space-y-4">
            {Object.entries(groupedChecks).map(([category, checks]) => (
              <div key={category} className="mb-4">
                <div className="flex items-center gap-2 mb-2 pb-1 border-b border-gray-200">
                  {getCategoryIcon(category)}
                  <h4 className="text-sm font-medium text-gray-700">
                    {formatCategory(category)}
                  </h4>
                  <span className="text-xs text-gray-500">
                    ({checks.filter(c => checklistData[c.id]?.completed).length}/{checks.length})
                  </span>
                </div>
                
                <div className="space-y-0.5">
                  {checks.map(check => {
                    const data = checklistData[check.id] || {};
                    
                    return (
                      <div 
                        key={check.id} 
                        className="group hover:bg-gray-50 rounded px-2 py-1.5 cursor-pointer transition-colors border-l-2 border-transparent hover:border-cyan-300"
                        onClick={(e) => {
                          // Only toggle if not clicking on input/select elements
                          if (!e.target.matches('input, select, button')) {
                            updateCheck(check.id, 'completed', !data.completed);
                          }
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 pt-0.5">
                            {data.completed ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <Circle className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <span className={`text-sm ${data.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                                  {check.label}
                                  {check.required !== false && <span className="text-red-500 ml-0.5 text-xs">*</span>}
                                </span>
                                {check.type && (
                                  <span className="text-xs text-gray-500 ml-2 inline-block px-1.5 py-0.5 bg-gray-100 rounded">
                                    {check.type}
                                  </span>
                                )}
                                {check.regulation && (
                                  <span className="text-xs text-gray-400 ml-2 hidden lg:inline">
                                    {check.regulation}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Value input if required - more compact */}
                            {check.recordValue && (
                              <div className="mt-1 ml-6">
                                {check.valueType === 'select' ? (
                                  <select
                                    value={data.value || ''}
                                    onChange={(e) => updateCheck(check.id, 'value', e.target.value)}
                                    className="w-full max-w-xs px-2 py-0.5 text-xs border border-gray-300 rounded"
                                  >
                                    <option value="">Select...</option>
                                    {check.options?.map(opt => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                ) : check.valueType === 'multifield' ? (
                                  <div className="grid grid-cols-3 gap-2">
                                    {check.fields?.map(field => (
                                      <input
                                        key={field.id}
                                        type="text"
                                        placeholder={field.placeholder}
                                        value={data[field.id] || ''}
                                        onChange={(e) => updateCheck(check.id, field.id, e.target.value)}
                                        className="px-2 py-0.5 text-xs border border-gray-300 rounded"
                                      />
                                    ))}
                                  </div>
                                ) : (
                                  <input
                                    type={check.valueType === 'number' ? 'number' : 'text'}
                                    value={data.value || ''}
                                    onChange={(e) => updateCheck(check.id, 'value', e.target.value)}
                                    placeholder={check.valuePlaceholder || 'Enter value'}
                                    className="w-full max-w-xs px-2 py-0.5 text-xs border border-gray-300 rounded"
                                  />
                                )}
                              </div>
                            )}
                            
                            {/* Show completion time inline */}
                            {data.completed && data.timestamp && (
                              <span className="text-xs text-gray-400 ml-6">
                                ✓ {new Date(data.timestamp).toLocaleTimeString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          
          {/* Custom checks section */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={addCustomCheck}
              className="text-sm text-cyan-600 hover:text-cyan-700"
            >
              + Add custom check
            </button>
          </div>
          
          {/* Completion warning */}
          {!isComplete && (
            <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
                <p className="text-sm text-yellow-800">
                  Complete all required checks (*) before departure.
                  These checks are legally required per UK maritime regulations.
                </p>
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={() => localStorage.setItem(`regulation_checklist_${voyageId}`, JSON.stringify(checklistData))}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Save className="h-4 w-4 inline mr-1" />
              Save Progress
            </button>
            <button
              onClick={handleComplete}
              disabled={!isComplete}
              className={`px-4 py-2 text-sm font-medium rounded-lg ${
                isComplete
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Confirm & Record
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegulationPreDepartureChecklist;